import React from 'react';
import { endsWith, replace, split, size } from 'lodash';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { device } from '../../../utils/breakpoints';
import { publicRuntimeConfig } from 'config';

const horizontalBreaker = (property, value) => css`${property}: ${(endsWith(value, '%') || endsWith(value, 'vw')) ? value : `${value}px`};`;
const verticalBreaker = (property, value) => css`${property}: ${(endsWith(value, '%') || endsWith(value, 'vh')) ? value : `${value}px`};`;

export const FlexContainer = styled.div`
    display: ${({ display = 'flex' }) => display};
    ${({ top }) => top && css`top: ${top}px`};
    ${({ direction, mx }) => direction === 'row' && mx && css`
        margin-left: ${mx};
        margin-right: ${mx};
    `}

    ${({ overflowY }) => overflowY && css`overflow-y: auto;`}
    ${({ overflowX }) => overflowX && css`overflow-x: auto;`}
    ${({ overflow }) => overflow && css`overflow: ${overflow};`}

    ${({ margin, marginHorizontal, marginVertical, mT = '0', mR = '0', mB = '0', mL = '0', mX, mY }) => {
        if (margin === 'auto') return css`margin: auto`;
        return css`margin: ${margin || marginVertical || mY || mT}px ${margin || marginHorizontal || mX || mR}px ${margin || marginVertical || mY || mB}px ${margin || marginHorizontal || mX || mL}px;`;
    }}

    ${props => props.gap && css`gap: ${props.gap}px;`}

    ${({ padding, paddingHorizontal, paddingVertical, pT = '0', pR = '0', pB = '0', pL = '0', pX, pY }) => `
        padding: ${padding || paddingVertical || pY || pT}px ${padding || paddingHorizontal || pX || pR}px ${padding || paddingVertical || pY || pB}px ${padding || paddingHorizontal || pX || pL}px;
    `}

    ${props => props.direction && css`flex-direction: ${props.direction};`}
    ${props => props.align && css`align-items: ${props.align};`}
    ${props => props.justify && css`justify-content: ${props.justify};`}
    ${({ center }) => center && css`
        align-items: center;
        justify-content: center;
    `}
    ${props => props.flex && css`flex: ${props.flex};`}
    ${({ width }) => width && css`width: ${(endsWith(width, '%') || endsWith(width, 'vw')) ? width : `${width}px`};`} //TODO arreglar
    ${({ height }) => height && css`height: ${(endsWith(height, '%') || endsWith(height, 'vh')) ? height : `${height}px`};`}
    ${({ borderRadius }) => {
        function resolverBorderRadius () {
            if (size(split(borderRadius, ' ')) > 1) return borderRadius;
            return `${borderRadius}px`;
        }
        if (borderRadius) return css`border-radius: ${resolverBorderRadius()};`;
    }}
    ${({ border }) => border && css`border: ${border};`}
    ${({ borderLeft }) => borderLeft && css`border-left: ${borderLeft};`}
    ${({ backgroundColor }) => backgroundColor && css`background-color: ${backgroundColor};`}

    ${({ shrink }) => (shrink || shrink === 0) && css`flex-shrink: ${shrink};`}
    ${({ grow }) => (grow || grow === 0) && css`flex-grow: ${grow};`}

    ${({ wrap = false }) => wrap && css`flex-wrap: wrap;`}

    ${({ position = false }) => position && css`position: ${position};`}

    ${({ pointer }) => pointer && css`
        cursor: pointer;
    `}

    ${({ maxWidth }) => maxWidth && horizontalBreaker('max-width', maxWidth)}
    ${({ minWidth }) => minWidth && css`min-width: ${minWidth}px;`}
    ${({ maxHeight }) => maxHeight && css`max-height: ${maxHeight};`}
    ${({ minHeight }) => minHeight && css`min-height: ${minHeight};`}

    ${({ fixedHeight }) => {
        if (fixedHeight) {
            const value = (endsWith(fixedHeight, '%') || endsWith(fixedHeight, 'vh')) ? fixedHeight : `${replace(fixedHeight, 'px', '')}px`;
            return css`
                min-height: ${value};
                max-height: ${value};
            `;
        }
    }}

    ${({ fixedWidth }) => {
        if (fixedWidth) {
            const value = endsWith(fixedWidth, '%') ? fixedWidth : `${replace(fixedWidth, 'px', '')}px`;
            return css`
                min-width: ${value};
                max-width: ${value};
            `;
        }
    }}

    ${({ fitContent }) => fitContent && css`width: fit-content;`}

    ${({ fitHeight }) => fitHeight && css`height: fit-content;`}

    ${({ zIndex }) => zIndex && css`z-index: ${zIndex};`}
    ${({ boxShadow }) => boxShadow && css`box-shadow: ${typeof boxShadow === 'boolean' ? '0px 2px 4px rgb(0 0 0 / 15%)' : boxShadow};`}
    ${({ textAlign }) => textAlign && css`text-align: ${textAlign};`}
    ${({ borderTop }) => borderTop && css`border-top: ${borderTop};`}
    ${({ borderBottom }) => borderBottom && css`border-bottom: ${borderBottom};`}
    ${({ borderRight }) => borderRight && css`border-right: ${borderRight};`}
    ${({ bottom }) => bottom && css`bottom: ${bottom}px;`}
    ${({ right }) => right && css`right: ${right}px;`}
`;

export const MainContainer = styled(FlexContainer)`
    width: 100%;
    overflow: hidden;
    position: relative;
    flex-grow: 1;
`;

export const Aside = styled.div`
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
    display: none;
    flex-direction: column;
    width: var(--sidebar-opened-width);
    z-index: 1;

    @media ${device.tablet} {
        display: flex;
    }

    ${({ background = '#333333' }) =>
        css`background: ${background};
    `}

    ${({ closedWidth = 'var(--sidebar-closed-width)', closed }) => closed && css`width: ${closedWidth};`}
`;

export const InnerAside = styled(Aside)`
    background: white;
    box-shadow: inset -4px 0px 4px 0px rgba(0,0,0,0.11);
`;

export const InnerAsideContent = styled.div`
    flex-grow: 1;
    overflow-y: ${({ overflowY = 'hidden' }) => overflowY};
`;

export const MainWrapper = styled.div`
    flex-grow: 1;
    outline: none;
    display: flex;
    flex-direction: column;
    overflow-y: ${({ overflowY = 'auto' }) => overflowY};
    background-color: ${({ background = 'white' }) => `#${background}`};
`;

export const MainContent = styled.div`
    background: red;
`;

export const Header = styled.div`
    display: flex;
    top: 0;
    z-index: 10;
    height: 55px;
    flex-shrink: 0;
    background-color: white;
    border-bottom: 1px solid #eaeaea;
    justify-content: center;
    align-items: center;

    ${props => props.sticky && css`position: sticky;`}
`;

export const FixedContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    top: 0;
    z-index: 1;
    flex-shrink: 0;
    ${props => props.bottom && css`
        bottom: 0;
    `}
`;

export const RootContainer = styled(FlexContainer)`
    position: absolute;
    display: flex;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    flex-direction: ${({ direction = 'row' }) => direction};
`;

export const Container = ({ withFooter = false, direction = 'column', style, children, ...props }) => {
    const styles = withFooter ? { ...style, paddingBottom: '50px' } : style;
    return (
        (

            <MainContainer {...props} direction={direction} style={{ ...styles }}>
                {children}
            </MainContainer>
        )
    );
};

export const InnerContainer = (props) => <Container {...props} style={{ paddingBottom: '0px' }} />;

export const Main = ({ withFooter, style, background, children, scrolleable, ...props }) => {
    // work around for footer with the current Main Container
    const styles = withFooter ? { ...style } : style;

    const FooterWrapper = styled(FlexContainer)`
    background-color: #f4f4f4;
    padding: 10px 0px;
  `;
    const Footer = ({ background }) => (
        <FooterWrapper justify='flex-end'>
            <FlexContainer style={{ height: '100%', marginRight: 20 }} align='center' >
                <span style={{ fontSize: '.8rem' }}>Backoffice {`${new Date().getFullYear()}`}</span>
            </FlexContainer>
        </FooterWrapper>
    );

    return (
        <>
            <MainWrapper {...props} background={background} style={{ ...styles }}>
                <FlexContainer grow={1} shrink={0} direction='column' overflowY={scrolleable} maxHeight={withFooter && 'calc(100% - 40px)'}>
                    {children}
                </FlexContainer>
                {withFooter && <Footer />}
            </MainWrapper>
        </>
    );
};

Main.defaultProps = {
    background: 'white',
    withFooter: false,
    scrolleable: false
};

Main.propTypes = {
    children: PropTypes.any,
    withFooter: PropTypes.bool
};
